import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleLoadSharpsringScriptUrl,
    handleSetSharpspringScript,
    handleSharpspringSubmit,
    setCaptchaEuScript,
} from '../utils';

Vue.component('partnership-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        stores: {
            value: Object,
        },
        captchaEuPublicKey: {
            value: String,
        },
        sharpspringBaseUrl: {
            value: String,
        },
        sharpspringEndpointId: {
            value: String,
        },
        sharpspringScriptUrl: {
            value: String,
        },
        isCaptchaEuEnabled: {
            value: Boolean,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: '',
            selected: '',
            selectedBrandStore: '',

            dropdownCountry: null,
            dropdownSubject: null,
            dropdownStore: null,

            // Dealer search (zip code vars)
            store: '',
            showZipSearchError: false,
            zipCoords: null,
            searchQueryIsDirty: false,
            nearbyProximityStores: null,
            // Error vars for zip api request
            needMoreInput: false,
            apiRequestLoading: false,
            countryValue: {
                mgMotorsNetherlands: 'NL',
                mgMotorsBelgiumNl: 'BE',
                mgMotorsBelgiumFr: 'BE',
                mgMotorsAustria: 'AT',
                mgMotorsDanmark: 'DK',
                mgMotorsGermany: 'DE',
                mgMotorsSpain: 'ES',
                mgMotorsFrance: 'FR',
                mgMotorsIceland: 'IS',
                mgMotorsItaly: 'IT',
                mgMotorsLuxembourgFr: 'LU',
                mgMotorsNorway: 'NO',
                mgMotorsPortugal: 'PT',
                mgMotorsSweden: 'SE',
                mgMotorsSwitzerlandIt: 'CH',
                mgMotorsSwitzerlandFr: 'CH',
                mgMotorsSwitzerlandDe: 'CH',
                mgMotorsFinland: 'FI',
                mgMotorsHungary: 'HU',
            },

            dropdownHeardFrom: null,
            heardFrom: null,

            dealerSearchCountries: [
                'mgMotorsItaly',
                'mgMotorsPortugal',
                'mgMotorsSpain',
            ],
        };
    },

    computed: {
        moreThenTwoStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 2;
        },

        hasBrandStores: function () {
            if (!this.countryHandle) {
                return false;
            }

            return Object.keys(this.stores).length > 0;
        },

        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },

        toggleButtonLoadingClass() {
            return this.submitting ? 'button--loading' : '';
        },
    },

    watch: {
        store() {
            if (this.store.length === 0) {
                this.selectedBrandStore = null;
            }

            if (this.store.length < 4) {
                this.needMoreInput = true;
                this.nearbyProximityStores = null;
                return;
            }

            if (this.selectedBrandStore) {
                if (this.selectedBrandStore.title === this.store) {
                    return;
                }
            }

            this.selectedBrandStore = null;
            this.needMoreInput = false;
            this.searchByZipGoogleApi();
        },

        countryHandle() {
            this.selectedBrandStore = null;
            this.dropdownStore = null;
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }
        handleSetSharpspringScript(
            this.siteHandle,
            this.sharpspringBaseUrl,
            this.sharpspringEndpointId,
            'enecoForm',
        );
        handleLoadSharpsringScriptUrl(
            this.siteHandle,
            this.sharpspringScriptUrl,
        );
        setCaptchaEuScript();
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        showDropdown(key) {
            if (!this.hasBrandStores) {
                return false;
            }

            if (!this.countryHandle) {
                return false;
            }
            if (this.countryHandle == key) {
                return true;
            }
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!this.isCaptchaEuEnabled) {
                    await this.handleDataSubmit(form);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(form, captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(form, captchaSolution) {
            try {
                const formData = new FormData(form);
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                const response = await axios.post(
                    '/promo-form/submit',
                    formData,
                );
                const result = response.data;
                const responseErrors = result.data.errors;

                if (responseErrors) {
                    this.errors.clear();
                    this.errors.record(responseErrors);

                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                // Success
                handleSharpspringSubmit(
                    this.siteHandle,
                    this.sharpspringEndpointId,
                );
                this.formCompleted = true;
                this.showZipSearchError = false;
                this.errors.clear();

                this.sendGTMData();

                window.location = result.data.redirectUrl;
                this.scrollToTop();
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData() {
            const country =
                this.siteHandle == 'default'
                    ? 'mgMotorsEurope'
                    : this.siteHandle;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.dataLayer.push({
                    event: 'lead',
                    lead_type: 'Eneco',
                    car_type: this.model,
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                });
            }
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        setDropdownCountry(event) {
            event.preventDefault();
            this.dropdownCountry = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.countryHandle = value;
        },

        setDropdownSubject(event) {
            this.dropdownSubject = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selected = value;
        },

        setDropdownStore(event) {
            this.dropdownStore = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selectedBrandStore = value;
        },
    },
});
